<script setup lang="ts">
/**
 * AppSidebar — двухпанельная навигация «rail + flyout» (2026-07-16, прототип v3).
 *
 * Левая узкая полоса (rail) показывает РАЗДЕЛЫ (иконка + название). Наведение
 * или клик по разделу раскрывает вбок flyout-панель с ПУНКТАМИ этого раздела,
 * сгруппированными подзаголовками (nav.group_*). Так на виду всегда ~10 разделов,
 * а не 40 пунктов, но любой пункт — в один наводящий жест.
 *
 * Три режима (как в прототипе v3):
 *   • Закреплено (по умолчанию) — rail развёрнут всегда, flyout активного
 *     раздела зарезервирован в раскладке (контент не перекрывается).
 *   • Откреплено — rail сворачивается до иконок (64px), а по наведению
 *     разворачивается + flyout ПОВЕРХ контента (максимум места конструкторам).
 *   • Переключение — кнопкой внизу rail или ⌘B. На узких экранах (<1024px)
 *     принудительно «откреплено», кнопка отключена.
 *
 * ★ Избранное — отдельный раздел-flyout: пункт с ★ попадает в него. «Недавние»
 * из сайдбара убраны — они в палитре ⌘K (CommandPalette). Модель навигации —
 * единый источник (navModel.ts); иконки привязаны к path здесь.
 */
import { RouterLink, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import {
  LayoutDashboard, Users, Filter, Workflow, FileText, Stethoscope, BarChart3, Mail, MailCheck, Smartphone, BellRing, MessagesSquare, Network,
  Radio, Send, Gift, Ticket, FlaskConical, Database, ArrowRightFromLine, Calculator, Wallet, Brain, Download,
  Megaphone, ShoppingBasket, GitBranchPlus, CheckSquare,
  ClipboardList, Headset,
  ScrollText, Key, KeyRound, Beaker, MousePointerClick,
  Sparkles, UserCog, Building2, CreditCard, LayoutTemplate,
  BookMarked, Package, Target, Bot, TrendingUp, Scale, BadgePercent, Crosshair, LayoutGrid, Boxes, Grid3x3,
  Inbox, ScanSearch, Wand2, ListTree, ShieldCheck, ShieldAlert, ShieldOff, RotateCw, Activity, Tags, CircleDot, Mailbox, Link2, DatabaseZap,
  Settings as SettingsIcon,
  ChevronRight, Star, Gamepad2,
  Pin, PinOff, Search, Newspaper, Palette, Layers, Webhook, Plug, Braces,
} from "lucide-vue-next";
import {
  DASHBOARD_ITEM, MAIN_SECTIONS, SETTINGS_GROUPS, ALL_NAV_ITEMS,
  type NavItemModel, type NavSectionModel,
} from "../../composables/navModel";
import { useNavFavorites } from "../../composables/useNavFavorites";
import { useAuthStore } from "../../stores/auth";

const { t } = useI18n();
const route = useRoute();
const auth = useAuthStore();

// Cmd+K palette lives in AppShell; sidebar только просит его открыть.
const emit = defineEmits<{ (e: "open-palette"): void }>();

// ★ Избранное (общее с палитрой ⌘K). trackVisit ведёт «недавние» для палитры —
// в самом сайдбаре недавние больше не показываем, но питать список надо.
const { favorites, isFavorite, toggleFavorite, trackVisit } = useNavFavorites();
watch(() => route.path, (p) => trackVisit(p), { immediate: true });

// path → модель пункта (резолвим избранное обратно в отображаемые пункты).
const ITEM_BY_PATH: Record<string, NavItemModel> = Object.fromEntries(
  ALL_NAV_ITEMS.map((i) => [i.path, { path: i.path, labelKey: i.labelKey, aliases: i.aliases, group: i.group }]),
);

// Иконки lucide — компоненты; модель навигации остаётся чистыми данными.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type IconComponent = any;

interface NavItem { to: string; icon: IconComponent; label: string; }
interface FlyGroup { label: string | null; items: NavItem[]; }
interface FlyModel { id: string; title: string; icon: IconComponent; groups: FlyGroup[]; isFavorites: boolean; }

/** Иконка на path (модель без Vue/lucide). Нет иконки — запасная CircleDot. */
const ICON_BY_PATH: Record<string, IconComponent> = {
  "/": LayoutDashboard,
  "/clients": Users,
  "/entities": Building2,
  "/segments": Filter,
  "/segments/overlap": CircleDot,
  "/segments/rfm": Grid3x3,
  "/exports": Download,
  "/external-segments": Database,
  "/preference-topics": Mail,
  "/broadcasts": Megaphone,
  "/bots": Bot,
  "/campaigns": Boxes,
  "/decision-campaigns": Crosshair,
  "/contact-policy": ShieldCheck,
  "/suppressions": ShieldOff,
  "/scenarios": Workflow,
  "/surveys": ClipboardList,
  "/operator-tasks": Headset,
  "/dialogs": MessagesSquare,
  "/messenger/spaces": Layers,
  "/chat-presentation": Palette,
  "/work-automations": Workflow,
  "/integration-connections": Plug,
  "/settings/code-library": Braces,
  "/referrals": Gift,
  "/segment-triggers": RotateCw,
  "/scenarios-oob": Sparkles,
  "/experiences": MousePointerClick,
  "/content-feeds": Newspaper,
  "/missions": Target,
  "/waiting-baskets": ShoppingBasket,
  "/approvals": CheckSquare,
  "/tester": Beaker,
  "/loyalty/tiers": Gift,
  "/loyalty/currencies": Wallet,
  "/loyalty/cashback": Wallet,
  "/loyalty/promotions": BadgePercent,
  "/loyalty/partners": Building2,
  "/loyalty/reports": BarChart3,
  "/loyalty/promo-pools": Ticket,
  "/loyalty/bonuses": Sparkles,
  "/loyalty/card": CreditCard,
  "/loyalty/card-templates": LayoutTemplate,
  "/loyalty/wallet-campaigns": Megaphone,
  "/loyalty/security": ShieldAlert,
  "/promo-codes": Ticket,
  "/templates": FileText,
  "/catalog-items": Package,
  "/dictionaries": BookMarked,
  "/ai-assistant": Bot,
  "/recommendations/test": Target,
  "/analytics": BarChart3,
  "/analytics/revenue": TrendingUp,
  "/analytics/deliverability": MailCheck,
  "/analytics/sms": Smartphone,
  "/analytics/push": BellRing,
  "/analytics/messengers": MessagesSquare,
  "/analytics/cross": Network,
  "/analytics/gamification": Gamepad2,
  "/event-log": Activity,
  "/outbox": Mailbox,
  "/experiments": FlaskConical,
  "/wow-stats": TrendingUp,
  "/diagnostics": Stethoscope,
  "/decision-policies": Scale,
  "/offers": BadgePercent,
  "/channel-templates": LayoutGrid,
  "/channels": Radio,
  "/channel-originators": Send,
  "/cascades": GitBranchPlus,
  "/automations/sources": Inbox,
  "/automations/recognisers": ScanSearch,
  "/automations/enrichments": Wand2,
  "/automations/policy": ShieldCheck,
  "/automations/events": ListTree,
  "/automations/replay": RotateCw,
  "/attribute-dictionary": Tags,
  "/ml-training": Brain,
  "/reverse-etl": ArrowRightFromLine,
  "/webhook-subscriptions": Webhook,
  "/data-connections": Link2,
  "/federated-segments": DatabaseZap,
  "/cost-estimate": Calculator,
  "/billing-showcase": Wallet,
  "/admin/users": UserCog,
  "/admin/tenants": Building2,
  "/api-tokens": Key,
  "/write-keys": KeyRound,
  "/audit-log": ScrollText,
  "/settings": SettingsIcon,
};

/** Иконка раздела (rail): по id раздела. Спец-разделы — dashboard/favorites.
 *  data/admin — секции бывших «Настроек», теперь отдельные пункты полосы. */
const SECTION_ICON: Record<string, IconComponent> = {
  audience: Users,
  segments: Filter,
  communications: Megaphone,
  engagement: Sparkles,
  loyalty: Gift,
  personalization: Crosshair,
  analytics: BarChart3,
  logs: ScrollText,
  data: Database,
  admin: SettingsIcon,
  dashboard: LayoutDashboard,
  favorites: Star,
};

function toNavItem(item: NavItemModel): NavItem {
  return { to: item.path, icon: ICON_BY_PATH[item.path] ?? CircleDot, label: t(item.labelKey) };
}

/** Раздел виден? Гейт по тонкому праву (requiresPermission) через auth.can(). */
function roleAllows(section: NavSectionModel): boolean {
  if (section.requiresPermission) return auth.can(section.requiresPermission);
  if (!section.requiresRole || section.requiresRole.length === 0) return true;
  const r = auth.role;
  return r != null && section.requiresRole.includes(r);
}
/** Пункт виден: нет requiresPermission ИЛИ право есть; requiresAnyPermission — любое из. */
function itemAllowed(item: NavItemModel): boolean {
  if (item.requiresPermission && !auth.can(item.requiresPermission)) return false;
  if (item.requiresAnyPermission && !item.requiresAnyPermission.some((p) => auth.can(p))) return false;
  return true;
}

/** Boundary-aware активность: /segments подсвечивает /segments/123, но не /scenarios. */
function isActive(to: string): boolean {
  if (to === "/") return route.path === "/";
  if (route.path === to) return true;
  return route.path.startsWith(to + "/");
}

// ─── rail: dashboard + разделы + избранное + настройки ───────────────────────
const dashboardItem = computed((): NavItem => toNavItem(DASHBOARD_ITEM));

/** Основные разделы rail — только те, где после гейтинга остались видимые пункты. */
const visibleSections = computed((): NavSectionModel[] =>
  MAIN_SECTIONS.filter(roleAllows).filter((s) => s.items.some(itemAllowed)));

/** Секции «Настроек» (Данные, Администрирование) — теперь отдельные пункты полосы,
 *  каждая со своим flyout. Показываем те, что разрешены и имеют видимые пункты. */
const visibleSettingsSections = computed((): NavSectionModel[] =>
  SETTINGS_GROUPS.filter(roleAllows)
    .filter((s) => s.items.some((i) => i.path !== "/settings" && itemAllowed(i))));

/** ★ избранное → отображаемые пункты (только известные пути). */
const favoriteItems = computed((): NavItem[] =>
  favorites.value
    .map((p) => ITEM_BY_PATH[p])
    .filter((m): m is NavItemModel => !!m)
    .map(toNavItem));

/** Раздел (main ИЛИ настройки) → flyout-модель: пункты гейтятся, /settings скрыт,
 *  соседние пункты с одинаковым group склеиваются под подзаголовком. */
function buildSectionFlyout(s: NavSectionModel): FlyModel {
  const groups: FlyGroup[] = [];
  for (const it of s.items.filter((i) => i.path !== "/settings" && itemAllowed(i))) {
    const label = it.group ? t(it.group) : null;
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(toNavItem(it));
    else groups.push({ label, items: [toNavItem(it)] });
  }
  return { id: s.id, title: t(s.labelKey), icon: SECTION_ICON[s.id] ?? CircleDot, isFavorites: false, groups };
}

/** Собрать flyout-модель по id (или null — у dashboard flyout нет). */
function buildFlyout(id: string | null): FlyModel | null {
  if (!id || id === "dashboard") return null;
  if (id === "favorites") {
    return { id, title: t("nav.favorites"), icon: Star, isFavorites: true, groups: [{ label: null, items: favoriteItems.value }] };
  }
  const s = MAIN_SECTIONS.find((x) => x.id === id) ?? SETTINGS_GROUPS.find((x) => x.id === id);
  return s ? buildSectionFlyout(s) : null;
}
function flyoutHasContent(m: FlyModel | null): boolean {
  return m != null && (m.isFavorites || m.groups.some((g) => g.items.length > 0));
}

/** Какой раздел «владеет» текущим маршрутом (для дефолтного flyout и подсветки). */
const routeSectionId = computed<string>(() => {
  if (route.path === "/") return "dashboard";
  for (const s of visibleSections.value) if (s.items.some((i) => isActive(i.path))) return s.id;
  for (const s of SETTINGS_GROUPS) if (s.items.some((i) => isActive(i.path))) return s.id;
  return "";
});

// ─── flyout второго уровня: всплывает по наведению/клику, сам сворачивается ───
// openId — раздел с открытым flyout (null = свёрнут). Второй уровень НЕ висит
// постоянно и не занимает место в раскладке — сворачивается при уходе курсора/
// фокуса и по клику вне меню (места на экране мало). hovering — курсор/фокус в
// зоне (чтобы в «откреплено» icon-rail разворачивался с подписями).
const navZoneRef = ref<HTMLElement | null>(null);
const openId = ref<string | null>(null);
const hovering = ref(false);

const flyout = computed<FlyModel | null>(() => buildFlyout(openId.value));
/** Подсветка «вы здесь» — раздел текущего маршрута; открытый flyout её не меняет. */
function rowActive(id: string): boolean { return routeSectionId.value === id; }

// ─── режимы «закреплено / откреплено» + узкий экран ──────────────────────────
const PIN_KEY = "cdp-sidebar-pinned";
const userPinned = ref(loadPinned());
const isNarrow = ref(false);
/** Эффективно закреплено — только если пользователь закрепил И экран не узкий. */
const pinned = computed(() => userPinned.value && !isNarrow.value);
/** rail развёрнут (с подписями): закреплено, либо наведено/раскрыто в «откреплено». */
const railExpanded = computed(() => pinned.value || hovering.value || openId.value != null);
/** flyout виден только когда открыт (наведение/клик) и в нём есть пункты. */
const flyoutVisible = computed(() => flyoutHasContent(flyout.value));

const RAIL_FULL = 240;
const RAIL_MINI = 64;
const FLYOUT_W = 272;
/** В раскладке резервируем ТОЛЬКО rail. flyout второго уровня — всегда всплывающий
 *  overlay поверх контента: места в раскладке не занимает, сворачивается при уходе
 *  курсора/фокуса или клике вне меню (экономим место на экране). */
const reservedWidth = computed(() => (pinned.value ? RAIL_FULL : RAIL_MINI));
const railWidth = computed(() => (railExpanded.value ? RAIL_FULL : RAIL_MINI));

function loadPinned(): boolean {
  try { return localStorage.getItem(PIN_KEY) !== "0"; } catch { return true; }
}
function togglePinned(): void {
  if (isNarrow.value) return;
  userPinned.value = !userPinned.value;
  try { localStorage.setItem(PIN_KEY, userPinned.value ? "1" : "0"); } catch { /* ignore */ }
  openId.value = null; hovering.value = false;
}

// ─── обработчики наведения / клика ──────────────────────────────────────────
let leaveTimer: ReturnType<typeof setTimeout> | null = null;
function onZoneEnter(): void {
  if (leaveTimer) { clearTimeout(leaveTimer); leaveTimer = null; }
  hovering.value = true;
}
/** Уход курсора из зоны — сворачиваем flyout (и icon-rail в «откреплено») с малой
 *  задержкой, чтобы успеть перевести курсор с раздела в саму панель. */
function onZoneLeave(): void {
  leaveTimer = setTimeout(() => { hovering.value = false; openId.value = null; }, 140);
}
function onZoneFocusOut(e: FocusEvent): void {
  const next = e.relatedTarget as Node | null;
  if (!next || !(e.currentTarget as HTMLElement).contains(next)) { hovering.value = false; openId.value = null; }
}
/** Навести/сфокусировать/кликнуть раздел — раскрыть его flyout (сам не навигирует). */
function openFlyout(id: string): void { openId.value = id; }
/** Свернуть flyout второго уровня. */
function closeFlyout(): void { openId.value = null; }
/** Переход по пункту flyout — свернуть меню. */
function onItemNavigate(): void { openId.value = null; hovering.value = false; }
/** Клик вне зоны навигации — свернуть flyout (не должен висеть и занимать место). */
function onDocPointerDown(e: Event): void {
  if (openId.value == null) return;
  const t = e.target as Node | null;
  if (!t || !navZoneRef.value?.contains(t)) closeFlyout();
}

// ─── клавиши: ⌘B переключает режим, Esc закрывает раскрытый flyout ───────────
function onKey(e: KeyboardEvent): void {
  const k = e.key.toLowerCase();
  if ((e.metaKey || e.ctrlKey) && k === "b") { e.preventDefault(); togglePinned(); }
  else if (e.key === "Escape" && openId.value != null) { closeFlyout(); }
}

// Авто-«откреплено» ниже lg (1024px); matchMedia держит вживую.
let mq: MediaQueryList | null = null;
const onNarrow = (e: MediaQueryListEvent): void => { isNarrow.value = e.matches; };
onMounted(() => {
  window.addEventListener("keydown", onKey);
  // Клик вне меню сворачивает flyout (capture — ловим до обработчиков контента).
  window.addEventListener("pointerdown", onDocPointerDown, true);
  try {
    mq = window.matchMedia("(max-width: 1023px)");
    isNarrow.value = mq.matches;
    mq.addEventListener?.("change", onNarrow);
  } catch { /* ignore */ }
});
onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKey);
  window.removeEventListener("pointerdown", onDocPointerDown, true);
  try { mq?.removeEventListener?.("change", onNarrow); } catch { /* ignore */ }
  if (leaveTimer) clearTimeout(leaveTimer);
});
</script>

<template>
  <!-- Аside = «распорка» раскладки (резервирует ширину). Реальный rail+flyout —
       абсолютно позиционированная зона поверх, чтобы в «откреплено» разворачиваться
       над контентом, а в «закреплено» — ровно занимать зарезервированное место. -->
  <aside
    data-testid="app-sidebar"
    :data-pinned="pinned ? '1' : '0'"
    :data-collapsed="railExpanded ? '0' : '1'"
    class="relative shrink-0 h-full bg-surface-0 transition-[width] duration-200 ease-out"
    :style="{ width: reservedWidth + 'px' }"
  >
    <div
      ref="navZoneRef"
      class="nav-zone absolute inset-y-0 left-0 z-40 flex items-stretch"
      data-testid="nav-zone"
      @mouseenter="onZoneEnter"
      @mouseleave="onZoneLeave"
      @focusout="onZoneFocusOut"
    >
      <!-- ── RAIL: разделы ──────────────────────────────────────────────── -->
      <nav
        data-testid="sidebar-rail"
        aria-label="Основное меню"
        class="flex flex-col h-full bg-surface-0 border-r border-surface-200 overflow-hidden transition-[width] duration-200 ease-out"
        :class="!pinned && railExpanded ? 'shadow-elevated' : ''"
        :style="{ width: railWidth + 'px' }"
      >
        <!-- Логотип -->
        <div class="h-16 shrink-0 flex items-center gap-2.5 border-b border-surface-200" :class="railExpanded ? 'px-3' : 'justify-center px-0'">
          <div class="size-8 rounded-lg bg-gradient-to-br from-accent-500 to-accent-700 flex items-center justify-center shrink-0 text-white">
            <svg class="size-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 7l5 10 3-6 3 6 5-10"/></svg>
          </div>
          <div v-if="railExpanded" class="flex flex-col leading-tight min-w-0">
            <span class="font-semibold text-surface-900 text-sm truncate">{{ t("app.title") }}</span>
            <span class="text-xs text-surface-400 truncate">{{ t("app.tagline") }}</span>
          </div>
        </div>

        <!-- Прокручиваемая середина -->
        <div class="flex-1 min-h-0 overflow-y-auto px-2 py-2 flex flex-col gap-1">
          <!-- Поиск (⌘K) -->
          <button
            type="button"
            data-testid="cmdk-trigger"
            class="w-full flex items-center rounded-lg border border-surface-200 bg-surface-50 hover:bg-surface-100 text-surface-500 transition-colors"
            :class="railExpanded ? 'gap-2 px-2.5 py-1.5' : 'justify-center py-2'"
            :title="railExpanded ? undefined : 'Поиск (⌘K)'"
            @click="emit('open-palette')"
          >
            <Search class="size-4 shrink-0" :stroke-width="2" />
            <span v-if="railExpanded" class="flex-1 text-left text-sm">Поиск</span>
            <kbd v-if="railExpanded" class="px-1.5 py-0.5 text-[10px] font-mono bg-surface-0 border border-surface-200 rounded text-surface-400">⌘K</kbd>
          </button>

          <!-- Сводка (прямая ссылка, без flyout) -->
          <RouterLink
            :to="dashboardItem.to"
            :class="[rowActive('dashboard') ? 'nav-item-active' : 'nav-item', 'w-full', railExpanded ? '' : 'justify-center px-0']"
            :title="railExpanded ? undefined : dashboardItem.label"
            @click="onItemNavigate"
          >
            <component :is="dashboardItem.icon" class="size-[18px] shrink-0" :stroke-width="2" />
            <span v-if="railExpanded" class="flex-1 truncate">{{ dashboardItem.label }}</span>
          </RouterLink>

          <!-- Избранное (flyout) -->
          <button
            type="button"
            :data-testid="'nav-row-favorites'"
            role="button"
            aria-haspopup="true"
            :aria-expanded="openId === 'favorites'"
            :class="[rowActive('favorites') ? 'nav-item-active' : 'nav-item', 'w-full text-left', railExpanded ? '' : 'justify-center px-0']"
            :title="railExpanded ? undefined : t('nav.favorites')"
            @mouseenter="openFlyout('favorites')"
            @focus="openFlyout('favorites')"
            @click="openFlyout('favorites')"
          >
            <Star class="size-[18px] shrink-0" :stroke-width="2" :class="favorites.length ? 'text-amber-500' : ''" :fill="favorites.length ? 'currentColor' : 'none'" />
            <span v-if="railExpanded" class="flex-1 truncate">{{ t("nav.favorites") }}</span>
            <ChevronRight v-if="railExpanded" class="size-3.5 shrink-0 opacity-50" :stroke-width="2.5" />
          </button>

          <div class="h-px bg-surface-200 my-1.5 mx-1" role="separator"></div>
          <div v-if="railExpanded" class="px-3 pt-0.5 pb-1 text-[10.5px] font-semibold uppercase tracking-wide text-surface-400">{{ t("nav.menu") }}</div>

          <!-- Разделы -->
          <button
            v-for="s in visibleSections"
            :key="s.id"
            type="button"
            :data-testid="'nav-row-' + s.id"
            role="button"
            aria-haspopup="true"
            :aria-expanded="openId === s.id"
            :class="[rowActive(s.id) ? 'nav-item-active' : 'nav-item', 'w-full text-left', railExpanded ? '' : 'justify-center px-0']"
            :title="railExpanded ? undefined : t(s.labelKey)"
            @mouseenter="openFlyout(s.id)"
            @focus="openFlyout(s.id)"
            @click="openFlyout(s.id)"
          >
            <component :is="SECTION_ICON[s.id] ?? CircleDot" class="size-[18px] shrink-0" :stroke-width="2" />
            <span v-if="railExpanded" class="flex-1 truncate">{{ t(s.labelKey) }}</span>
            <ChevronRight v-if="railExpanded" class="size-3.5 shrink-0 opacity-50" :stroke-width="2.5" />
          </button>
        </div>

        <!-- Подвал: секции настроек (Данные, Администрирование) + переключатель режима -->
        <div class="shrink-0 border-t border-surface-200 px-2 py-2 flex flex-col gap-1">
          <button
            v-for="s in visibleSettingsSections"
            :key="s.id"
            type="button"
            :data-testid="'nav-row-' + s.id"
            role="button"
            aria-haspopup="true"
            :aria-expanded="openId === s.id"
            :class="[rowActive(s.id) ? 'nav-item-active' : 'nav-item', 'w-full text-left', railExpanded ? '' : 'justify-center px-0']"
            :title="railExpanded ? undefined : t(s.labelKey)"
            @mouseenter="openFlyout(s.id)"
            @focus="openFlyout(s.id)"
            @click="openFlyout(s.id)"
          >
            <component :is="SECTION_ICON[s.id] ?? CircleDot" class="size-[18px] shrink-0" :stroke-width="2" />
            <span v-if="railExpanded" class="flex-1 truncate">{{ t(s.labelKey) }}</span>
            <ChevronRight v-if="railExpanded" class="size-3.5 shrink-0 opacity-50" :stroke-width="2.5" />
          </button>

          <button
            type="button"
            data-testid="sidebar-pin-toggle"
            class="w-full flex items-center gap-2 rounded-lg py-2 text-sm text-surface-500 hover:bg-surface-100 hover:text-surface-900 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            :class="railExpanded ? 'px-3' : 'justify-center px-0'"
            :disabled="isNarrow"
            :title="pinned ? t('sidebar.unpin_menu') : t('sidebar.pin_menu')"
            @click="togglePinned"
          >
            <component :is="pinned ? Pin : PinOff" class="size-4 shrink-0" :stroke-width="2" />
            <span v-if="railExpanded">{{ pinned ? t("sidebar.unpin_menu") : t("sidebar.pin_menu") }}</span>
          </button>
        </div>
      </nav>

      <!-- ── FLYOUT: пункты выбранного раздела ──────────────────────────── -->
      <aside
        v-if="flyoutVisible && flyout"
        data-testid="nav-flyout"
        :data-flyout="flyout.id"
        class="flex flex-col h-full bg-surface-0 border-r border-surface-200 shadow-elevated overflow-y-auto"
        :style="{ width: FLYOUT_W + 'px' }"
      >
        <!-- Заголовок раздела -->
        <div class="h-16 shrink-0 flex items-center gap-2.5 px-4 border-b border-surface-200">
          <component :is="flyout.icon" class="size-5 shrink-0 text-accent-600" :stroke-width="2" />
          <span class="font-semibold text-surface-900 text-[15px] truncate">{{ flyout.title }}</span>
        </div>

        <div class="flex-1 min-h-0 overflow-y-auto px-2 py-2">
          <!-- Пустое избранное -->
          <div v-if="flyout.isFavorites && !favoriteItems.length" class="px-4 py-8 text-center text-surface-400 text-[12.5px] leading-relaxed flex flex-col items-center gap-2">
            <Star class="size-6 opacity-50" :stroke-width="1.8" />
            <span>{{ t("nav.favorites_empty") }}</span>
          </div>

          <!-- Группы пунктов -->
          <template v-else>
            <div v-for="(g, gi) in flyout.groups" :key="gi" class="mb-1">
              <div v-if="g.label" class="px-3 pt-2.5 pb-1 text-[10.5px] font-semibold uppercase tracking-wide text-surface-400">{{ g.label }}</div>
              <div v-for="item in g.items" :key="item.to" class="group relative">
                <RouterLink
                  :to="item.to"
                  :class="[isActive(item.to) ? 'nav-item-active' : 'nav-item', 'pr-9']"
                  @click="onItemNavigate"
                >
                  <component :is="item.icon" class="size-4 shrink-0" :stroke-width="2" />
                  <span class="flex-1 truncate">{{ item.label }}</span>
                </RouterLink>
                <button
                  type="button"
                  data-testid="nav-pin-toggle"
                  class="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-md transition-opacity hover:text-amber-500"
                  :class="isFavorite(item.to) ? 'opacity-100 text-amber-500' : 'opacity-0 group-hover:opacity-100 text-surface-300'"
                  :title="isFavorite(item.to) ? t('nav.unpin') : t('nav.pin')"
                  @click.prevent.stop="toggleFavorite(item.to)"
                >
                  <Star class="size-3.5" :fill="isFavorite(item.to) ? 'currentColor' : 'none'" />
                </button>
              </div>
            </div>
          </template>
        </div>
      </aside>
    </div>
  </aside>
</template>
